import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  AxisPointerComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import type { EChartsType } from 'echarts/core'
import { useT } from '../../../app/lib/i18n'
import { vars } from '../../styles/theme.css'
import { buildTimeSeriesOption, readChartTheme } from './buildOption'
import type { TimeSeriesChartProps } from './types'
import * as s from './TimeSeriesChart.css'

echarts.use([
  LineChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  AxisPointerComponent,
  CanvasRenderer,
])

export type { TimeSeriesChartProps, TimeSeriesPoint, TimeSeriesSeries } from './types'

const THEME_TOKENS = {
  fg: vars.color.fg,
  mutedFg: vars.color.fgSecondary,
  border: vars.color.border,
  card: vars.color.card,
}

export function TimeSeriesChart({
  series,
  loading = false,
  emptyMessage = 'No data to chart',
  onLegendToggle,
  className,
  height = 360,
  xDomain = null,
  hour12 = true,
}: TimeSeriesChartProps) {
  const t = useT()
  const rootRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const instanceRef = useRef<EChartsType | null>(null)
  const onLegendToggleRef = useRef(onLegendToggle)
  const seriesRef = useRef(series)
  const xDomainRef = useRef(xDomain)
  const hour12Ref = useRef(hour12)
  onLegendToggleRef.current = onLegendToggle
  seriesRef.current = series
  xDomainRef.current = xDomain
  hour12Ref.current = hour12

  const isEmpty = !loading && series.length === 0
  const heightStyle = typeof height === 'number' ? `${height}px` : height

  // Init + dispose + resize
  useEffect(() => {
    const el = chartRef.current
    if (!el) return

    const chart = echarts.init(el, undefined, { renderer: 'canvas' })
    instanceRef.current = chart

    const onLegendSelectChanged = (params: unknown) => {
      const p = params as { name?: string; selected?: Record<string, boolean> }
      if (!p.name || !p.selected) return
      const visible = p.selected[p.name] !== false
      const match = seriesRef.current.find((s) => s.label === p.name)
      if (match) onLegendToggleRef.current?.(match.id, visible)
    }
    chart.on('legendselectchanged', onLegendSelectChanged)

    const ro = new ResizeObserver(() => {
      chart.resize()
    })
    ro.observe(el)

    return () => {
      chart.off('legendselectchanged', onLegendSelectChanged)
      ro.disconnect()
      chart.dispose()
      instanceRef.current = null
    }
  }, [])

  // Update option when series or domain change
  useEffect(() => {
    const chart = instanceRef.current
    const root = rootRef.current
    if (!chart) return
    const theme = readChartTheme(root, THEME_TOKENS)
    chart.setOption(buildTimeSeriesOption(series, theme, xDomain, hour12), { notMerge: true })
  }, [series, xDomain, hour12])

  // Re-theme when document theme class / data-theme flips
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const apply = () => {
      const chart = instanceRef.current
      if (!chart) return
      const theme = readChartTheme(root, THEME_TOKENS)
      chart.setOption(
        buildTimeSeriesOption(seriesRef.current, theme, xDomainRef.current, hour12Ref.current),
        { notMerge: true },
      )
    }

    const observer = new MutationObserver(apply)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    })
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={rootRef}
      className={[s.root, className].filter(Boolean).join(' ')}
      style={{ height: heightStyle }}
      role="img"
      aria-label={t("shared.chart.timeSeries")}
    >
      <div ref={chartRef} className={s.canvas} style={{ height: '100%', width: '100%' }} />
      {loading && <div className={s.skeletonAnimated} aria-hidden="true" />}
      {isEmpty && (
        <div className={s.overlay}>
          <span className={s.overlayMessage}>{emptyMessage}</span>
        </div>
      )}
    </div>
  )
}
